import { featuredWorkoutToCardWorkout } from './featured-workout-adapter'
import { FeaturedWorkoutHoverPreview } from './featured-workout-hover-preview'
import { WorkoutCard } from '../ui/workout-card'
import React, { useMemo, useState } from 'react'
import { FeaturedWorkout } from 'src/dao/core-dao'
import { cn } from 'src/lib/utils'
import { FeaturedSurface } from 'src/service/featured-analytics-service'

interface FeaturedWorkoutCardProps {
  workout: FeaturedWorkout
  onAction: () => void
  /**
   * Opt-in muted hover preview. On for the public sign-in shelf, off on Home: the preview is a
   * shop window for people who have not signed up, and three extra HLS streams on a page an
   * existing member visits daily is cost without purpose.
   */
  withPreview?: boolean
  /**
   * Fill the parent's box instead of holding the card system's fixed `aspect-[16/9]`.
   *
   * The shelf sizes cards by width and lets the aspect decide their height. The hero panel is
   * the other way round: it has a fixed 100vh to divide up, so its cards are sized by height.
   * Without this they overflow the viewport and reintroduce the scroll the panel exists to
   * remove. `cn` is tailwind-merge, so `aspect-auto` here really does replace the base class.
   */
  fill?: boolean
  /**
   * Size the card's text to the card's own width instead of the card system's fixed type scale.
   *
   * That scale — a 36px/40px title over a 30px meta row — needs a 16:9 card **at least ~250px
   * wide**, because the box is only `width * 9 / 16` tall and `Content`'s inner column is
   * `flex-1`, whose `min-height: auto` lets it grow straight through the bottom of the card
   * (`overflow-hidden` then clips it).
   *
   * Measured on the phone rail with a two-line title: at a 320px viewport the card is 212x119
   * and the trainer name and duration badge hung 15px past its bottom edge, cut in half. At
   * 360px and 375px they stayed inside the card but consumed all 12px of its padding, sitting
   * flush against the edge; a 390px phone cleared it by 4px. So one width clipped and the rest
   * were a longer title away from it.
   *
   * `cqw` rather than a breakpoint because the constraint is the *card's* width, not the
   * viewport's: the rail caps its cards at 320px, so past a certain viewport the card stops
   * growing and a `min-width` rule would keep scaling the type it holds. The ratio is set by the
   * narrowest case — 11cqw clears a 320px viewport with ~12px to spare and reaches the original
   * 36px by the time the card is at its 320px cap.
   */
  compact?: boolean
  /** Passed through to the preview so its event says which shelf it fired from. */
  surface: FeaturedSurface
  className?: string
}

/**
 * A card on the featured shelf.
 *
 * Composed from the house card system (`component/ui/workout-card`) rather than
 * `component/programmes/programme-workout-card`, which resolves trainer, duration label and
 * date visibility through `SessionContext` and so cannot render on the login page at all.
 *
 * Composed by hand rather than reusing `WorkoutCard.FlexFlow` for one reason: `FlexFlow` uses
 * `WorkoutCard.Meta`, whose `Trainer` defaults to resolving the name through the authed
 * `/trainers` route. Here the name is already in the payload, so `Trainer` runs in `payload`
 * mode and the shelf makes no authed request — which is what lets it render for a signed-out
 * visitor. The visual result matches `FlexFlow`, so the shelf sits at the same weight as its
 * neighbours.
 *
 * Nothing here renders locked, spent, greyed out or counted, and there is no dismissal
 * control: a featured workout is simply playable.
 */
export const FeaturedWorkoutCard = ({
  workout,
  onAction,
  withPreview = false,
  fill = false,
  compact = false,
  surface,
  className,
}: FeaturedWorkoutCardProps): React.ReactElement => {
  const cardWorkout = useMemo(() => featuredWorkoutToCardWorkout(workout), [workout])
  const [isHovered, setIsHovered] = useState(false)

  return (
    // The whole card IS the play control — there is no separate button — so there is one
    // testid, not two. Specs click the card. `data-workout-id` lets a spec assert *which*
    // workouts came back and in what order without matching on title copy.
    <div
      data-testid="featured-workout-card"
      data-workout-id={workout.id}
      className="h-full"
      onMouseEnter={withPreview ? () => setIsHovered(true) : undefined}
      onMouseLeave={withPreview ? () => setIsHovered(false) : undefined}
    >
      <WorkoutCard
        workout={cardWorkout}
        className={cn(
          'flex-1 h-full rounded-md',
          fill && 'aspect-auto',
          // The query container for the `cqw` sizes below — it has to be the card box itself,
          // since that is the width the type is being scaled against.
          compact && '[container-type:inline-size]',
          className,
        )}
        onAction={onAction}
      >
        <WorkoutCard.Image>
          {/* Inside `Image`, which wraps its children in `WorkoutCardOverlay` (absolute
              inset-0) — so the preview stacks over the poster with no new positioning CSS,
              and `Content` below stays on top of it via its own `relative`. */}
          {withPreview ? (
            <FeaturedWorkoutHoverPreview
              videoUrl={workout.videoUrl}
              active={isHovered}
              workoutId={workout.id}
              surface={surface}
            />
          ) : null}
          <WorkoutCard.Content>
            <div className="flex-1 flex flex-col justify-end">
              {/* `length:` is not decoration — without the hint both Tailwind and tailwind-merge
                  can read an arbitrary `text-[…]` as a colour, which would leave `text-4xl`
                  standing and silently undo the whole thing. */}
              <WorkoutCard.Title className={compact ? 'text-[length:clamp(20px,11cqw,36px)] leading-[1.05]' : undefined} />
              {/* Carries a testid because it is the row that gets clipped when the type scale
                  outgrows the box, and clipping is invisible to `toBeInViewport` — an ancestor's
                  `overflow-hidden` does not change an element's bounding rect. The spec compares
                  this rect against the card's. */}
              <div
                data-testid="featured-workout-card-meta"
                className={cn('flex items-center justify-between', compact ? 'mt-1' : 'mt-2')}
              >
                {/* `truncate` only in compact mode, and as a guard rather than a style: the name
                    is free to wrap in a card with room, but a second line here is 28px the box
                    does not have, and it would clip the badge again. */}
                <WorkoutCard.Trainer
                  source="payload"
                  className={compact ? 'text-[length:clamp(13px,6.5cqw,20px)] truncate' : undefined}
                />
                <WorkoutCard.Duration />
              </div>
            </div>
          </WorkoutCard.Content>
        </WorkoutCard.Image>
      </WorkoutCard>
    </div>
  )
}
